import { useMemo } from 'react'

import { useAutomatedMessageContext } from '@liveHelper/components/AutomatedMessageProvider'
import {
  closeWidgetTimeSelector,
  conversationSelector,
  paramsSelector,
  useStore,
} from '@liveHelper/models/store'

import { isDebug } from '@core/debug'
import { safeConsoleTable } from '@core/logging/helpers'

interface ReturnProps {
  isCloseFeatureEnabled: boolean
  widgetShouldBeVisible: boolean
  canCloseWidget: boolean
}

/**
 * Returns the whether the close widget feature is enabled.
 */
export const useCloseWidgetEnabled = (): ReturnProps => {
  const params = useStore(paramsSelector)
  const isCloseFeatureEnabled = Boolean(params.close_widget === true)

  const conversation = useStore(conversationSelector)
  const closeWidgetTime = useStore(closeWidgetTimeSelector)
  const { automatedMessage } = useAutomatedMessageContext()

  const { widgetShouldBeVisible, canCloseWidget, debugReason } = useMemo(() => {
    // Disabled when the params.close_widget is off
    if (!isCloseFeatureEnabled) {
      return {
        widgetShouldBeVisible: true,
        canCloseWidget: false,
        debugReason: 'params.close_widget disabled',
      }
    }

    // Disabled when the visitor is in a conversation
    if (conversation) {
      return {
        widgetShouldBeVisible: true,
        canCloseWidget: false,
        debugReason: 'conversation',
      }
    }

    // Disabled when there is an automated message
    if (automatedMessage) {
      return {
        widgetShouldBeVisible: true,
        canCloseWidget: false,
        debugReason: 'automated message',
      }
    }

    // Hide widget when closed in the last hour
    // eslint-disable-next-line react-hooks/purity -- pre-existing impure call in render; behavior-preserving fix deferred
    if (closeWidgetTime && closeWidgetTime > Date.now() - 60 * 60 * 1000) {
      return {
        widgetShouldBeVisible: false,
        canCloseWidget: true,
        debugReason: 'closed in last hour',
      }
    }

    return {
      widgetShouldBeVisible: true,
      canCloseWidget: true,
      debugReason: 'not closed or closed more than 1 hour ago',
    }
  }, [isCloseFeatureEnabled, conversation, automatedMessage, closeWidgetTime])

  if (isDebug()) {
    safeConsoleTable({
      close_widget_time: closeWidgetTime
        ? new Date(closeWidgetTime).toISOString()
        : undefined,
      widget_should_be_visible: widgetShouldBeVisible,
      can_close_widget: canCloseWidget,
      debug_reason: debugReason,
    })
  }

  return {
    isCloseFeatureEnabled,
    widgetShouldBeVisible,
    canCloseWidget,
  }
}
